import {
    ChangeDetectionStrategy, Component, EventEmitter, Input, Output,
    inject, signal, computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import {
    Firestore, addDoc, collection, serverTimestamp
} from '@angular/fire/firestore';
import { ApprovalService } from '../../../services/approval.service';
import { AuthService } from '../../../services/auth.service';
import { DepartmentSchemesService, DepartmentEntry } from '../../../services/department-schemes.service';

interface ImportRow {
    rowIdx: number;
    projectName: string;
    schemeName: string;
    department: string;
    block: string;
    gp: string;
    yearOfCreation: string;
    estimatedCost: number;
    amountSpent: number;
    roadLength: number | null;
    status: string;
    progress: number;
    hmPriority: boolean;
    recurringIntervention: boolean;
    executantName: string;
    contactNumber: string;
    remarks: string;
    errors: string[];
    isDuplicate: boolean;
}

type ImportStage = 'idle' | 'preview' | 'submitting' | 'done';

@Component({
    selector: 'app-bulk-import-modal',
    standalone: true,
    imports: [CommonModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
@if (isOpen) {
  <div class="bim-overlay" (click)="cancel()">
    <div class="bim-card" (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="bim-header">
        <div class="bim-title-wrap">
          <div class="bim-icon"><i class="fa-solid fa-file-import"></i></div>
          <div>
            <h3>Bulk Import Projects</h3>
            <p>
              @switch (stage()) {
                @case ('idle')       { Upload an Excel or CSV file. Each row will be sent for approval to your department approver. }
                @case ('preview')    { Review {{ validCount() }} of {{ rows().length }} valid rows · {{ duplicateCount() }} duplicate(s) · {{ errorCount() }} with errors. }
                @case ('submitting') { Sending {{ submittedCount() }} of {{ validCount() }} rows for approval… }
                @case ('done')       { Finished. {{ submittedCount() }} row(s) sent for approval, {{ failedCount() }} failed. }
              }
            </p>
          </div>
        </div>
        <button class="bim-close" (click)="cancel()"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <!-- Body -->
      <div class="bim-body">

        <!-- Idle: drag-drop + sample template -->
        @if (stage() === 'idle') {
          <div class="bim-drop"
               [class.dragging]="isDragging()"
               (dragover)="onDragOver($event)"
               (dragleave)="onDragLeave()"
               (drop)="onDrop($event)">
            <i class="fa-solid fa-cloud-arrow-up"></i>
            <p>Drag & drop your .xlsx, .xls or .csv file here</p>
            <span>— or —</span>
            <label class="bim-pick-btn">
              <i class="fa-solid fa-folder-open"></i> Choose a file
              <input type="file" accept=".xlsx,.xls,.csv"
                     (change)="onFileInput($event)" hidden />
            </label>
            @if (fileError()) {
              <div class="bim-error"><i class="fa-solid fa-circle-exclamation"></i> {{ fileError() }}</div>
            }
          </div>

          <div class="bim-template">
            <div>
              <strong>Need the template?</strong>
              <p>Download the sample file — columns match the New Project Request form exactly.</p>
            </div>
            <button class="bim-btn-secondary" (click)="downloadTemplate()">
              <i class="fa-solid fa-file-excel"></i> Download Template
            </button>
          </div>
        }

        <!-- Preview table -->
        @if (stage() === 'preview' || stage() === 'submitting' || stage() === 'done') {
          <div class="bim-table-wrap">
            <table class="bim-table">
              <thead>
                <tr>
                  <th class="num">#</th>
                  <th>Project Name</th>
                  <th>Scheme</th>
                  <th>Dept</th>
                  <th>Block</th>
                  <th>GP Name</th>
                  <th>Year</th>
                  <th class="num">Cost (₹L)</th>
                  <th class="num">Road (km)</th>
                  <th>Status</th>
                  <th>Flags</th>
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                @for (r of rows(); track r.rowIdx) {
                  <tr [class.row-error]="r.errors.length > 0"
                      [class.row-dup]="r.isDuplicate">
                    <td class="num">{{ r.rowIdx }}</td>
                    <td>{{ r.projectName || '—' }}</td>
                    <td>
                      <span class="bim-chip">{{ abbreviate(r.schemeName) || '—' }}</span>
                      <small *ngIf="r.schemeName">{{ r.schemeName }}</small>
                    </td>
                    <td>{{ r.department || '—' }}</td>
                    <td>{{ r.block || '—' }}</td>
                    <td>{{ r.gp || '—' }}</td>
                    <td>{{ r.yearOfCreation || '—' }}</td>
                    <td class="num">{{ r.estimatedCost || 0 }}</td>
                    <td class="num">{{ r.roadLength != null ? r.roadLength : '—' }}</td>
                    <td><span class="bim-pill" [ngClass]="statusClass(r.status)">{{ r.status || 'Planned' }}</span></td>
                    <td>
                      @if (r.hmPriority) { <span class="bim-flag hm">HM</span> }
                      @if (r.recurringIntervention) { <span class="bim-flag rec">RI</span> }
                    </td>
                    <td>
                      @if (r.errors.length === 0 && !r.isDuplicate) {
                        <span class="bim-status-ok"><i class="fa-solid fa-circle-check"></i> Ready</span>
                      } @else if (r.isDuplicate) {
                        <span class="bim-status-warn" title="Duplicate within this import"><i class="fa-solid fa-clone"></i> Duplicate</span>
                      } @else {
                        <span class="bim-status-err" [title]="r.errors.join('; ')">
                          <i class="fa-solid fa-circle-exclamation"></i> {{ r.errors[0] }}
                          @if (r.errors.length > 1) { <small>(+{{ r.errors.length - 1 }})</small> }
                        </span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (stage() === 'submitting') {
            <div class="bim-progress">
              <div class="bim-progress-bar"><div class="bim-progress-fill"
                                                  [style.width.%]="submittedCount() / Math.max(1, validCount()) * 100"></div></div>
              <span>{{ submittedCount() }} / {{ validCount() }}</span>
            </div>
          }

          @if (stage() === 'done') {
            <div class="bim-summary">
              <div class="bim-summary-stat ok">
                <i class="fa-solid fa-paper-plane"></i>
                <span>{{ submittedCount() }} row(s) sent for approval</span>
              </div>
              @if (failedCount() > 0) {
                <div class="bim-summary-stat err">
                  <i class="fa-solid fa-circle-xmark"></i>
                  <span>{{ failedCount() }} row(s) failed — see console</span>
                </div>
              }
              <p class="bim-summary-hint">
                Each row has been queued in <code>pending_approvals</code> and routed to your
                department approver. Approved projects will appear in the live dashboard automatically.
              </p>
            </div>
          }
        }
      </div>

      <!-- Footer -->
      <div class="bim-footer">
        <button class="bim-btn-secondary" (click)="cancel()" [disabled]="stage() === 'submitting'">
          {{ stage() === 'done' ? 'Close' : 'Cancel' }}
        </button>
        @if (stage() === 'preview') {
          <button class="bim-btn-secondary" (click)="resetIdle()">
            <i class="fa-solid fa-arrow-left"></i> Pick another file
          </button>
          <button class="bim-btn-primary" (click)="submitAll()" [disabled]="validCount() === 0">
            <i class="fa-solid fa-paper-plane"></i>
            Submit {{ validCount() }} row(s) for Approval
          </button>
        }
        @if (stage() === 'done' && failedCount() > 0) {
          <button class="bim-btn-primary" (click)="resetIdle()">
            <i class="fa-solid fa-rotate-right"></i> Try another file
          </button>
        }
      </div>
    </div>
  </div>
}
`,
    styles: [`
:host { display: block; }
.bim-overlay {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
  padding: 16px;
}
.bim-card {
  background: white;
  width: 1080px;
  max-width: 96vw;
  max-height: 92vh;
  display: flex; flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
}
.bim-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 18px 22px;
  border-bottom: 1px solid #f1f5f9;
}
.bim-title-wrap { display: flex; gap: 12px; align-items: center; }
.bim-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: #eef2ff; color: #4338ca;
  display: flex; align-items: center; justify-content: center;
  font-size: 17px;
}
.bim-header h3 { font-size: 16px; font-weight: 800; color: #0f172a; margin: 0; }
.bim-header p  { font-size: 12px; color: #64748b; margin: 2px 0 0; max-width: 720px; }
.bim-close {
  width: 32px; height: 32px; border-radius: 8px;
  border: none; background: #f8fafc; color: #64748b; cursor: pointer;
  &:hover { background: #f1f5f9; color: #0f172a; }
}
.bim-body { padding: 18px 22px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 14px; }

/* Drag-drop area */
.bim-drop {
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  padding: 36px 24px;
  text-align: center;
  background: #f8fafc;
  transition: border-color 0.15s, background 0.15s;
  &.dragging { border-color: #4338ca; background: #eef2ff; }
  i { font-size: 32px; color: #94a3b8; display: block; margin-bottom: 10px; }
  p { font-size: 14px; font-weight: 600; color: #475569; margin: 0 0 6px; }
  span { color: #94a3b8; font-size: 12px; }
}
.bim-pick-btn {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 10px;
  padding: 9px 18px;
  background: #1e40af; color: white;
  border-radius: 9px;
  font-weight: 700; font-size: 13px;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
}
.bim-error {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 14px;
  padding: 6px 12px; border-radius: 8px;
  background: #fef2f2; color: #b91c1c;
  font-size: 12.5px; font-weight: 600;
}
.bim-template {
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 14px 16px;
  background: #f0fdf4; border: 1px solid #bbf7d0;
  border-radius: 12px;
  strong { color: #166534; font-size: 13px; }
  p { margin: 2px 0 0; font-size: 12px; color: #15803d; }
}

/* Table */
.bim-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  max-height: 460px;
  overflow: auto;
}
.bim-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}
.bim-table thead th {
  position: sticky; top: 0;
  background: #0b2664;
  color: #e2e8f0;
  font-weight: 700; font-size: 10.5px; letter-spacing: 0.3px;
  text-transform: uppercase;
  text-align: left;
  padding: 9px 10px;
  border-bottom: 2px solid #0a1f4d;
  &.num { text-align: right; }
  white-space: nowrap;
}
.bim-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: top;
  small { display: block; color: #94a3b8; font-size: 10.5px; margin-top: 2px; }
  &.num { text-align: right; font-variant-numeric: tabular-nums; }
}
.bim-table tbody tr.row-error { background: #fef2f2; }
.bim-table tbody tr.row-dup   { background: #fff7ed; }
.bim-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #eef2ff; color: #3730a3;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.3px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
}
.bim-pill {
  display: inline-block;
  padding: 2px 9px; border-radius: 9999px;
  font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.3px;
  &.s-planned    { background: #f1f5f9; color: #475569; }
  &.s-inprogress { background: #dbeafe; color: #1d4ed8; }
  &.s-completed  { background: #dcfce7; color: #15803d; }
  &.s-stuck      { background: #fee2e2; color: #b91c1c; }
}
.bim-flag {
  display: inline-block;
  padding: 1px 7px; margin-right: 4px;
  border-radius: 4px;
  font-size: 9.5px; font-weight: 800; letter-spacing: 0.3px;
  &.hm  { background: #faf5ff; color: #7e22ce; border: 1px solid #e9d5ff; }
  &.rec { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
}
.bim-status-ok   { color: #15803d; font-weight: 700; font-size: 11px; }
.bim-status-warn { color: #c2410c; font-weight: 700; font-size: 11px; }
.bim-status-err  { color: #b91c1c; font-weight: 700; font-size: 11px; }

/* Progress */
.bim-progress {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 0;
  .bim-progress-bar {
    flex: 1; height: 8px;
    background: #f1f5f9; border-radius: 9999px; overflow: hidden;
  }
  .bim-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #3b82f6, #10b981);
    border-radius: 9999px;
    transition: width 0.3s ease;
  }
  span { font-size: 12px; font-weight: 700; color: #475569; font-variant-numeric: tabular-nums; }
}

/* Summary */
.bim-summary {
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  .bim-summary-stat {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 700;
    margin-bottom: 4px;
    &.ok  { color: #15803d; }
    &.err { color: #b91c1c; }
  }
  .bim-summary-hint { margin: 8px 0 0; font-size: 12px; color: #64748b; line-height: 1.45;
    code { background: #eef2ff; color: #3730a3; padding: 1px 5px; border-radius: 4px; font-size: 11px; }
  }
}

/* Footer */
.bim-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 14px 22px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfd;
}
.bim-btn-primary, .bim-btn-secondary {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 18px;
  border-radius: 9px;
  border: none;
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.bim-btn-primary { background: #1e40af; color: white;
  &:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); } }
.bim-btn-secondary { background: #f1f5f9; color: #334155;
  &:hover:not(:disabled) { background: #e2e8f0; } }
`]
})
export class BulkImportModalComponent {
    @Input() isOpen = false;
    @Output() closed = new EventEmitter<{ submitted: number; failed: number } | null>();

    private approvalService = inject(ApprovalService);
    private authService = inject(AuthService);
    private deptService = inject(DepartmentSchemesService);
    private firestore = inject(Firestore);

    // expose Math to template
    Math = Math;

    stage = signal<ImportStage>('idle');
    rows = signal<ImportRow[]>([]);
    isDragging = signal(false);
    fileError = signal<string | null>(null);
    fileName = signal<string>('');
    submittedCount = signal(0);
    failedCount = signal(0);

    validCount = computed(() =>
        this.rows().filter(r => r.errors.length === 0 && !r.isDuplicate).length
    );
    duplicateCount = computed(() => this.rows().filter(r => r.isDuplicate).length);
    errorCount = computed(() => this.rows().filter(r => r.errors.length > 0).length);

    // ── File handling ─────────────────────────────────────────────────────
    onFileInput(e: Event): void {
        const f = (e.target as HTMLInputElement).files?.[0];
        if (f) this.processFile(f);
    }

    onDragOver(e: DragEvent): void { e.preventDefault(); this.isDragging.set(true); }
    onDragLeave(): void { this.isDragging.set(false); }
    onDrop(e: DragEvent): void {
        e.preventDefault();
        this.isDragging.set(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) this.processFile(f);
    }

    private processFile(file: File): void {
        this.fileError.set(null);
        const lower = file.name.toLowerCase();
        if (!lower.endsWith('.xlsx') && !lower.endsWith('.xls') && !lower.endsWith('.csv')) {
            this.fileError.set('Please pick an .xlsx, .xls or .csv file.');
            return;
        }
        this.fileName.set(file.name);
        const reader = new FileReader();
        reader.onload = e => {
            try {
                const data = new Uint8Array(e.target!.result as ArrayBuffer);
                const wb   = XLSX.read(data, { type: 'array', cellDates: true });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const records = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });
                const parsed = records.map((r, i) => this.parseRow(r, i + 2));   // +2 → 1-based + header row
                this.runValidation(parsed);
                this.rows.set(parsed);
                this.stage.set('preview');
            } catch (err) {
                console.error('[bulk-import] parse error:', err);
                this.fileError.set('Could not read this file. Make sure it is a valid Excel/CSV.');
            }
        };
        reader.readAsArrayBuffer(file);
    }

    /** Parse one row from XLSX object into our normalized shape. */
    private parseRow(r: Record<string, any>, rowIdx: number): ImportRow {
        // Strip all non-alphanumeric chars for fuzzy column matching
        const nk = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const k = (...keys: string[]): any => {
            for (const key of keys) {
                const nKey = nk(key);
                for (const actual of Object.keys(r)) {
                    if (nk(actual) === nKey) return r[actual];
                }
            }
            return '';
        };

        const bool = (v: any): boolean => {
            if (typeof v === 'boolean') return v;
            const s = String(v ?? '').trim().toLowerCase();
            return s === 'true' || s === 'yes' || s === '1' || s === 'y';
        };
        const num = (v: any): number => {
            const n = Number(String(v ?? '').replace(/[,\s]/g, ''));
            return Number.isFinite(n) ? n : 0;
        };
        const numOrNull = (v: any): number | null => {
            const raw = String(v ?? '').trim();
            if (!raw) return null;
            const n = Number(raw.replace(/[,\s]/g, ''));
            return Number.isFinite(n) ? n : null;
        };

        return {
            rowIdx,
            projectName:           String(k('Project Name', 'ProjectName', 'Name') ?? '').trim(),
            schemeName:            String(k('Scheme Name', 'SchemeName', 'Scheme') ?? '').trim(),
            department:            String(k('Department', 'Dept') ?? '').trim(),
            block:                 String(k('Block', 'Block/Division', 'BlockDivision', 'Division') ?? '').trim(),
            gp:                    String(k('GP Name', 'GP', 'GramPanchayat', 'Village', 'Location') ?? '').trim(),
            yearOfCreation:        String(k('Year of Creation', 'YearOfCreation', 'Sanction Year', 'SanctionYear', 'Year') ?? '').trim(),
            estimatedCost:         num(k('Estimated Cost (₹L)', 'Estimated Cost', 'EstimatedCost', 'Cost')),
            amountSpent:           num(k('Amount Spent (₹L)', 'Amount Spent', 'AmountSpent', 'Spent')),
            roadLength:            numOrNull(k('Road Length (km)', 'Road Length', 'RoadLength', 'Length (km)', 'Length')),
            status:                String(k('Status') ?? 'Planned').trim() || 'Planned',
            progress:              num(k('Progress %', 'Progress', 'Physical')),
            hmPriority:            bool(k('HM Priority', 'HMPriority')),
            recurringIntervention: bool(k('Recurring Intervention', 'RecurringIntervention', 'Recurring')),
            executantName:         String(k('Executant Name', 'ExecutantName', 'Contractor') ?? '').trim(),
            contactNumber:         String(k('Contact Number', 'ContactNumber', 'Contact') ?? '').trim(),
            remarks:               String(k('Remarks') ?? '').trim(),
            errors: [],
            isDuplicate: false,
        };
    }

    private runValidation(rows: ImportRow[]): void {
        const knownDepts = new Set(
            this.deptService.getDepartments().map((d: DepartmentEntry) => d.name.toLowerCase())
        );
        const userDepts = new Set(
            (this.authService.currentUser()?.departmentIds ?? [])
                .map(id => id.toLowerCase())
        );

        const seen = new Set<string>();    // dedupe key within this import
        for (const r of rows) {
            // Required fields
            if (!r.projectName) r.errors.push('Project Name is required');
            if (!r.schemeName)  r.errors.push('Scheme Name is required');
            if (!r.department)  r.errors.push('Department is required');

            // Department recognised
            if (r.department && !knownDepts.has(r.department.toLowerCase())) {
                r.errors.push(`Unknown department "${r.department}"`);
            }

            // Department assigned to the user
            if (r.department && userDepts.size > 0) {
                const normId = r.department.toLowerCase()
                    .replace(/[\s&]/g, '_').replace(/[^a-z0-9_]/g, '').replace(/_+/g, '_');
                if (!userDepts.has(normId) && !userDepts.has(r.department.toLowerCase())) {
                    r.errors.push(`You are not assigned to department "${r.department}"`);
                }
            }

            // Numeric range
            if (r.progress < 0 || r.progress > 100) r.errors.push('Progress must be 0–100');
            if (r.estimatedCost < 0) r.errors.push('Estimated Cost cannot be negative');

            // Strict mutex between HM Priority & Recurring Intervention
            if (r.hmPriority && r.recurringIntervention) {
                r.errors.push('HM Priority and Recurring Intervention are mutually exclusive');
            }

            // Duplicate detection within this import
            const key = `${r.department.toLowerCase()}::${r.schemeName.toLowerCase()}::${r.block.toLowerCase()}::${r.projectName.toLowerCase()}`;
            if (seen.has(key)) {
                r.isDuplicate = true;
            } else {
                seen.add(key);
            }
        }
    }

    // ── Submission ───────────────────────────────────────────────────────
    async submitAll(): Promise<void> {
        const valid = this.rows().filter(r => r.errors.length === 0 && !r.isDuplicate);
        if (valid.length === 0) return;

        this.stage.set('submitting');
        this.submittedCount.set(0);
        this.failedCount.set(0);

        // Optional batch-history doc (best-effort; OK if rules block it).
        const batchId = await this.recordImportBatch(valid.length).catch(() => null);

        for (const r of valid) {
            try {
                await this.approvalService.enqueueChange({
                    targetCollection: 'projects',
                    operation: 'create',
                    payload: {
                        name:                   r.projectName,
                        dept:                   r.department,
                        scheme:                 r.schemeName,
                        loc:                    r.gp || r.block || 'Dhamnagar',
                        block:                  r.block || '',
                        gp:                     r.gp || '',
                        yearOfCreation:         r.yearOfCreation || '',
                        cost:                   r.estimatedCost,
                        spent:                  r.amountSpent,
                        physical:               r.progress,
                        roadLength:             r.roadLength ?? null,
                        status:                 this.normalizeStatus(r.status),
                        hmPriority:             r.hmPriority,
                        recurringIntervention:  r.recurringIntervention,
                        priority:               false,
                        executant: {
                            name:    r.executantName || '',
                            contact: r.contactNumber || '',
                        },
                        remarks:                r.remarks || '',
                        start:                  new Date().toISOString().slice(0, 10),
                        end:                    new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
                    },
                    remarks: batchId
                        ? `Bulk import row ${r.rowIdx} (batch ${batchId})`
                        : `Bulk import row ${r.rowIdx}`,
                });
                this.submittedCount.update(n => n + 1);
            } catch (err) {
                console.error(`[bulk-import] row ${r.rowIdx} failed:`, err);
                this.failedCount.update(n => n + 1);
            }
        }
        this.stage.set('done');
    }

    /** Best-effort batch-history record. Falls through silently if rules deny it. */
    private async recordImportBatch(rowCount: number): Promise<string | null> {
        try {
            const user = this.authService.currentUser();
            const ref  = await addDoc(collection(this.firestore, 'pending_imports'), {
                fileName:    this.fileName(),
                importedBy:  user?.uid ?? null,
                importedByName: user?.displayName ?? user?.email ?? null,
                totalRows:   this.rows().length,
                validRows:   rowCount,
                duplicateRows: this.duplicateCount(),
                errorRows:   this.errorCount(),
                createdAt:   serverTimestamp(),
                status:      'SUBMITTED',
            });
            return ref.id;
        } catch (err) {
            console.warn('[bulk-import] could not record import history:', err);
            return null;
        }
    }

    // ── Navigation ───────────────────────────────────────────────────────
    cancel(): void {
        if (this.stage() === 'submitting') return;
        this.closed.emit(
            this.stage() === 'done'
                ? { submitted: this.submittedCount(), failed: this.failedCount() }
                : null
        );
        this.resetIdle();
    }

    resetIdle(): void {
        this.stage.set('idle');
        this.rows.set([]);
        this.fileError.set(null);
        this.fileName.set('');
        this.submittedCount.set(0);
        this.failedCount.set(0);
    }

    // ── Template download ─────────────────────────────────────────────────
    downloadTemplate(): void {
        // Columns match the New Project Request form field order exactly
        const header = [
            'Project Name',
            'Scheme Name',
            'Department',
            'Block',
            'GP Name',
            'Year of Creation',
            'Estimated Cost (₹L)',
            'Amount Spent (₹L)',
            'Road Length (km)',
            'Status',
            'Progress %',
            'Executant Name',
            'Contact Number',
            'Remarks',
            'HM Priority',
            'Recurring Intervention',
        ];
        const sample1 = [
            'Construction of Road from Anandapur to Market',
            'Central Road and Infrastructure Fund (CRIF)',
            'Works',
            'Dhamanagar',
            'Anandapur',
            '2024-25',
            100, 25,
            2.5,
            'In Progress', 30,
            'Ramesh Kumar', '9876543210',
            'Foundation work completed',
            'no', 'no',
        ];
        const sample2 = [
            'Renovation of Community Hall Tihidi GP',
            'Pradhan Mantri Gram Sadak Yojana (PMGSY)',
            'Works',
            'Tihidi',
            'Tihidi',
            '2025-26',
            250, 0,
            '',
            'Planned', 0,
            '', '',
            '',
            'yes', 'no',
        ];
        const ws = XLSX.utils.aoa_to_sheet([header, sample1, sample2]);
        ws['!cols'] = [
            { wch: 44 }, // Project Name
            { wch: 36 }, // Scheme Name
            { wch: 14 }, // Department
            { wch: 16 }, // Block
            { wch: 16 }, // GP Name
            { wch: 16 }, // Year of Creation
            { wch: 18 }, // Estimated Cost (₹L)
            { wch: 16 }, // Amount Spent (₹L)
            { wch: 15 }, // Road Length (km)
            { wch: 12 }, // Status
            { wch: 10 }, // Progress %
            { wch: 22 }, // Executant Name
            { wch: 14 }, // Contact Number
            { wch: 30 }, // Remarks
            { wch: 12 }, // HM Priority
            { wch: 20 }, // Recurring Intervention
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Projects');
        XLSX.writeFile(wb, 'ppms-bulk-import-template.xlsx');
    }

    // ── helpers ──────────────────────────────────────────────────────────
    statusClass(status: string): string {
        switch ((status || 'Planned').toLowerCase()) {
            case 'completed':   return 'bim-pill s-completed';
            case 'in progress': return 'bim-pill s-inprogress';
            case 'stuck':       return 'bim-pill s-stuck';
            default:            return 'bim-pill s-planned';
        }
    }

    abbreviate(name: string): string {
        if (!name) return '';
        const paren = name.match(/\(([A-Z][A-Z0-9]+)\)/);
        if (paren) return paren[1];
        return name.split(/\s+/).filter(w => /^[A-Z]/.test(w)).slice(0, 4).map(w => w[0]).join('');
    }

    private normalizeStatus(s: string): 'Planned' | 'In Progress' | 'Completed' | 'Stuck' {
        const v = (s || '').trim().toLowerCase();
        if (v === 'completed' || v === 'done')       return 'Completed';
        if (v === 'in progress' || v === 'inprogress' || v === 'ongoing') return 'In Progress';
        if (v === 'stuck' || v === 'delayed' || v === 'blocked')          return 'Stuck';
        return 'Planned';
    }
}
